import { useEffect, useState } from "react";

export const IOS_APP_STORE_URL =
  "https://apps.apple.com/us/app/bite-recipes/id6745537938";

export const ANDROID_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.sarahyiskah.bite";

const DOWNLOAD_PATH = "/download";
const APP_SHARE_URL = "bite://share";

const getUserAgent = () => {
  if (typeof navigator === "undefined") {
    return "";
  }

  return navigator.userAgent || "";
};

/** "ios" | "android" | "" for desktop (or no navigator). */
export const getMobilePlatform = () => {
  const userAgent = getUserAgent();
  if (/Android/i.test(userAgent)) {
    return "android";
  }
  if (
    /iPhone|iPad|iPod/i.test(userAgent) ||
    (typeof navigator !== "undefined" &&
      navigator.platform === "MacIntel" &&
      navigator.maxTouchPoints > 1)
  ) {
    return "ios";
  }
  return "";
};

export const isCrawlerUserAgent = () =>
  /bot|crawler|spider|facebookexternalhit|facebot|twitterbot|slackbot|discordbot|linkedinbot|pinterest|telegrambot|whatsapp|applebot|googlebot|bingbot/i.test(
    getUserAgent(),
  );

const normalizeAttributionId = value =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const getCampaignAttribution = () => {
  if (typeof window === "undefined") {
    return { campaignId: "", contentId: "" };
  }

  const params = new URLSearchParams(window.location.search);
  const campaignId = normalizeAttributionId(
    params.get("campaign_id") ||
      params.get("campaignId") ||
      params.get("src") ||
      params.get("campaign") ||
      params.get("utm_campaign") ||
      "",
  );
  const contentId = normalizeAttributionId(
    params.get("content_id") ||
      params.get("contentId") ||
      params.get("content") ||
      params.get("utm_content") ||
      "",
  );
  return { campaignId, contentId };
};

const appendAttributionParams = (url, attribution) => {
  const { campaignId, contentId } = attribution;
  if (campaignId) {
    url.searchParams.set("src", campaignId);
    url.searchParams.set("campaign_id", campaignId);
  }
  if (contentId) {
    url.searchParams.set("content_id", contentId);
  }
  return url;
};

const getTrackedDownloadUrl = () => {
  if (typeof window === "undefined") {
    return "";
  }

  const attribution = getCampaignAttribution();
  if (!attribution.campaignId) {
    return "";
  }

  const url = new URL(DOWNLOAD_PATH, window.location.origin);
  return appendAttributionParams(url, attribution).toString();
};

export const getDownloadInfo = () => {
  const platform = getMobilePlatform();
  const trackedDownloadUrl = getTrackedDownloadUrl();

  if (platform === "android") {
    return {
      label: "Download on Google Play",
      url: trackedDownloadUrl || ANDROID_PLAY_STORE_URL,
    };
  }

  if (platform === "ios") {
    return {
      label: "Download on the App Store",
      url: trackedDownloadUrl || IOS_APP_STORE_URL,
    };
  }

  return {
    label: "Download Bite",
    url: trackedDownloadUrl || IOS_APP_STORE_URL,
  };
};

const DEFAULT_DOWNLOAD_INFO = {
  label: "Download Bite",
  url: IOS_APP_STORE_URL,
};

// Pre-rendered pages must match the first client render, so device- and
// campaign-specific links are resolved after mount instead of during render.
export const useDownloadInfo = () => {
  const [downloadInfo, setDownloadInfo] = useState(DEFAULT_DOWNLOAD_INFO);

  useEffect(() => {
    setDownloadInfo(getDownloadInfo());
  }, []);

  return downloadInfo;
};

export const getOpenShareAppUrl = () => {
  const userAgent = getUserAgent();

  if (/Android/i.test(userAgent)) {
    const fallback = getTrackedDownloadUrl() || ANDROID_PLAY_STORE_URL;
    return `intent://share#Intent;scheme=bite;package=com.sarahyiskah.bite;S.browser_fallback_url=${encodeURIComponent(
      fallback,
    )};end`;
  }

  return APP_SHARE_URL;
};

const setQueryParams = (url, query) => {
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });
  return url;
};

const getOpenEntityAppUrl = (entity, id, query = {}) => {
  const encodedId = encodeURIComponent(id);
  const appUrl = setQueryParams(new URL(`bite://${entity}/${encodedId}`), query);
  appendAttributionParams(appUrl, getCampaignAttribution());
  const userAgent = getUserAgent();

  if (/Android/i.test(userAgent)) {
    const appPath = appUrl.toString().replace(/^bite:\/\//i, "");
    const fallback = getTrackedDownloadUrl() || ANDROID_PLAY_STORE_URL;
    return `intent://${appPath}#Intent;scheme=bite;package=com.sarahyiskah.bite;S.browser_fallback_url=${encodeURIComponent(
      fallback,
    )};end`;
  }

  return appUrl.toString();
};

const getAutoOpenEntityAppUrl = (entity, webPath, id, query = {}) => {
  const userAgent = getUserAgent();
  if (!/Android/i.test(userAgent)) {
    return getOpenEntityAppUrl(entity, id, query);
  }

  const encodedId = encodeURIComponent(id);
  let currentUrl = ANDROID_PLAY_STORE_URL;

  if (typeof window !== "undefined") {
    const url = new URL(`/${webPath}/${encodedId}`, window.location.origin);
    url.searchParams.set("autoOpen", "0");
    setQueryParams(url, query);
    appendAttributionParams(url, getCampaignAttribution());
    currentUrl = url.toString();
  }

  const appPath = getOpenEntityAppUrl(entity, id, query)
    .replace(/^intent:\/\//i, "")
    .split("#Intent;")[0];
  return `intent://${appPath}#Intent;scheme=bite;package=com.sarahyiskah.bite;S.browser_fallback_url=${encodeURIComponent(
    currentUrl,
  )};end`;
};

export const getOpenAppUrl = recipeId =>
  getOpenEntityAppUrl("recipe", recipeId);

export const getAutoOpenAppUrl = recipeId =>
  getAutoOpenEntityAppUrl("recipe", "r", recipeId);

export const getOpenUserAppUrl = userId =>
  getOpenEntityAppUrl("user", userId);

export const getAutoOpenUserAppUrl = userId =>
  getAutoOpenEntityAppUrl("user", "u", userId);

// Collection links carry the join token (`k`) through to the app.
export const getOpenCollectionAppUrl = (collectionId, token = "") =>
  getOpenEntityAppUrl("collection", collectionId, { k: token });

export const getAutoOpenCollectionAppUrl = (collectionId, token = "") =>
  getAutoOpenEntityAppUrl("collection", "c", collectionId, { k: token });
